import {
  ActionType,
  EmptyListingButtonTypes,
  InfoBlockProps,
  InfoBlockStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingSelectOption,
  SelectionMode,
  SortDirections,
  ViewSelectOption,
} from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { ListSubscriptionsInstructionsPanel } from "../../components/InstructionsPanel/InstructionsPanel";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { PageId } from "../../constants/pluginConstants";
import {
  basicNoRefreshCaching,
  emptyChar,
  GroupingKeys,
  MonochromeIconIds,
  SvgIconIds,
  ViewKeys,
} from "../../constants/uiConstants";
import {
  AzureCloudLinkCollection,
  AzureCloudLinkSummary,
  AzureSubscriptionCollection,
  AzureSubscriptionSummary,
} from "../../gen/clients/mchub-azure-api-client";
import { optimizedRetryOption, responseItemstoArray, statusSortComparator } from "../../helpers/resourceHelper";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { CloudLinkAddRegionProps, newCloudLinkAddRegion } from "../../operations/CloudLink/CloudLinkAddRegion";
import {
  CloudLinkSubscriptionLinkProps,
  newCloudLinkSubscriptionLink,
} from "../../operations/CloudLink/CloudLinkSubscriptionLink";
import { chainFunctions, defaultListCloudLinksRegion, getAzureLocationName } from "../../utils";

const enum LinkStatusEnum {
  LINKED = "LINKED",
  UNLINKED = "UNLINKED",
  CREATING = "CREATING",
  UPDATING = "UPDATING"
}

export interface SubscriptionLinkItem extends AzureSubscriptionSummary {
  status: LinkStatusEnum;
  ociTenancyName?: string;
  locations?: string[];
  cloudLink?: AzureCloudLinkSummary;
}

enum ColumnIds {
  Name = "name",
  Id = "id",
  LinkStatus = "status",
  OciTenancyName = "ociTenancyName",
  Locations = "locations"
}

enum ActionIds {
  LinkSubscription = "link-subscription",
  AddRegion = "add-region",
  Refresh = "refresh",
  Help = "help",
}

const enum NewSidePanels {
  GET_STARTED = "GET_STARTED",
}

export const CloudLinkList = (): JSX.Element => {
  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { locale } = React.useContext<Settings>(SettingsContext);

  const [selection, setSelection] = React.useState<SubscriptionLinkItem[]>([]);
  const [sidePanelOpen, setSidePanelOpen] = React.useState<NewSidePanels | undefined>(undefined);

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent>();

  const { perPageItemsCount } = useRowCount();

  const {
    response: subscriptionsResponse,
    loading: subscriptionsLoading,
    refresh: subscriptionsRefresh,
  } = useQueryCall({
    method: apiClients.azResourceApi.listAzureSubscriptions,
    options: { args: {}, caching: basicNoRefreshCaching, fetchAllPages: true, retry: optimizedRetryOption },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadSubscription(),
      },
    },
  });
  const subscriptionItems = subscriptionsResponse
    && responseItemstoArray<AzureSubscriptionCollection, AzureSubscriptionSummary>(subscriptionsResponse);

  const {
    response: cloudLinksResponse,
    loading: cloudLinksLoading,
    refresh: cloudLinksRefresh,
  } = useQueryCall({
    method: apiClients.withRegion(defaultListCloudLinksRegion).cloudLinkApi.listAzureCloudLinks,
    options: { args: {}, caching: basicNoRefreshCaching, fetchAllPages: true, retry: optimizedRetryOption },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadCloudLinks(),
      },
    },
  });
  const activeCloudLinkItemsForLinking = (
    cloudLinksResponse && responseItemstoArray<AzureCloudLinkCollection, AzureCloudLinkSummary>(cloudLinksResponse)
  )?.filter(
    cloudLink => cloudLink.lifecycleState === "ACTIVE"
      || cloudLink.lifecycleState === "UPDATING"
      || cloudLink.lifecycleState === "CREATING",
  );
  const activeCloudLinkItemsForRegionAdd = activeCloudLinkItemsForLinking?.filter(
    cloudLink => (cloudLink.ociTenancyPaymentModel
      ? cloudLink.ociTenancyPaymentModel !== "FREE"
      : cloudLink.ociTenancyType !== "PROMO" && cloudLink.ociTenancyType !== "PROMO_CONSUMED"),
  );

  const refresh = (): void => {
    subscriptionsRefresh();
    cloudLinksRefresh();
  };

  const getLinkStatusStr = (status: LinkStatusEnum): string => {
    switch (status) {
      case LinkStatusEnum.LINKED:
        return Messages.subscriptionStatus.linked();
      case LinkStatusEnum.UPDATING:
        return Messages.subscriptionStatus.updating();
      case LinkStatusEnum.CREATING:
        return Messages.subscriptionStatus.linking();
      case LinkStatusEnum.UNLINKED:
      default:
        return Messages.subscriptionStatus.unlinked();
    }
  };

  const getLinkStatus = (value: SubscriptionLinkItem): JSX.Element => (
    <span>{getLinkStatusStr(value.status)}</span>
  );

  const getLinkLocations = (value: SubscriptionLinkItem): JSX.Element => {
    const locationNames = value.locations?.map(
      loc => getAzureLocationName(loc),
    );
    return <span>{locationNames && locationNames.length > 0 ? locationNames.join(", ") : emptyChar}</span>;
  };

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.Name,
      name: Messages.labels.subscription(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <AzureSubscriptionLink
          resourceId=""
          subscriptionId={value.id}
          subscriptionName={value.name}
        />
      ),
    },
    {
      itemProp: ColumnIds.Id,
      name: Messages.labels.subscriptionId(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
    },
    {
      itemProp: ColumnIds.LinkStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: SubscriptionLinkItem,
        b: SubscriptionLinkItem,
      ) => statusSortComparator<SubscriptionLinkItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getLinkStatus(value),
    },
  ];
  if (activeCloudLinkItemsForLinking && activeCloudLinkItemsForLinking.length > 1) {
    columns.push({
      itemProp: ColumnIds.OciTenancyName,
      name: Messages.labels.ociTenant(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
    });
  }
  columns.push({
    itemProp: ColumnIds.Locations,
    name: Messages.labels.locations(),
    flexGrow: 1,
    initialSortDirection: SortDirections.ASC,
    isResizable: true,
    onRenderItems: value => getLinkLocations(value),
  });

  const groupingOptions: ListingSelectOption[] = [
    {
      key: GroupingKeys.NoGrouping,
      text: Messages.listCloudLink.filters.noGrouping(),
    },
  ];

  const viewOptions: ViewSelectOption[] = [
    {
      key: ViewKeys.ListView,
      text: Messages.listCloudLink.filters.listView(),
      icon: MonochromeIconIds.List,
    },
  ];

  const items = React.useMemo(() => {
    const linkItems: SubscriptionLinkItem[] = [];
    if (subscriptionItems && activeCloudLinkItemsForLinking) {
      subscriptionItems.forEach((item: AzureSubscriptionSummary) => {
        const linkedSubscription = activeCloudLinkItemsForLinking?.find(
          cloudLink => cloudLink.subscriptionIds?.includes(item.id),
        );
        linkItems.push({
          ...item,
          status: linkedSubscription?.lifecycleState === "ACTIVE"
            ? LinkStatusEnum.LINKED
            : linkedSubscription?.lifecycleState === "CREATING"
              ? LinkStatusEnum.CREATING
              : linkedSubscription?.lifecycleState === "UPDATING"
                ? LinkStatusEnum.UPDATING
                : LinkStatusEnum.UNLINKED,
          ociTenancyName: linkedSubscription?.ociTenancyName,
          locations: linkedSubscription?.locations,
          cloudLink: linkedSubscription,
        });
      });
    }
    return linkItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(subscriptionItems), JSON.stringify(activeCloudLinkItemsForLinking)]);

  // eslint-disable-next-line max-len
  const { trigger: triggerLinkSubscription } = useOperation<CloudLinkSubscriptionLinkProps>(newCloudLinkSubscriptionLink);
  const { trigger: triggerAddRegion } = useOperation<CloudLinkAddRegionProps>(newCloudLinkAddRegion);

  const { closePanels } = useSidePanel();

  const cloudLinkActions: ActionType[] = [
    {
      key: ActionIds.LinkSubscription,
      text: Messages.actions.linkSubscription(),
      icon: MonochromeIconIds.Create,
      disabled: !selection.length || selection[0].status !== LinkStatusEnum.UNLINKED,
      onClick: () => {
        setSidePanelOpen(undefined);
        trackActionClick(ActionIds.LinkSubscription, PageId.CLOUDLINK_LIST);
        triggerLinkSubscription({
          targetId: ActionIds.LinkSubscription,
          subscription: selection[0],
          activeCloudLinks: activeCloudLinkItemsForLinking || [],
          onExecute: () => {
            listingComponentRef?.resetTableSelection();
            refresh();
          },
          onCancel: () => trackActionDiscard(ActionIds.LinkSubscription, PageId.CLOUDLINK_LIST),
        });
      },
    },
    {
      key: ActionIds.AddRegion,
      text: Messages.actions.addRegion(),
      icon: MonochromeIconIds.Create,
      disabled: !activeCloudLinkItemsForRegionAdd?.length,
      onClick: () => {
        setSidePanelOpen(undefined);
        trackActionClick(ActionIds.AddRegion, PageId.CLOUDLINK_LIST);
        triggerAddRegion({
          targetId: ActionIds.AddRegion,
          showTenantOptions: !!activeCloudLinkItemsForLinking && activeCloudLinkItemsForLinking.length > 1,
          activeCloudLinks: activeCloudLinkItemsForLinking || [],
          onExecute: () => {
            listingComponentRef?.resetTableSelection();
            refresh();
          },
          onCancel: () => trackActionDiscard(ActionIds.AddRegion, PageId.CLOUDLINK_LIST),
        });
      },
    },
    {
      key: ActionIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.CLOUDLINK_LIST);
        chainFunctions(refresh, listingComponentRef?.resetTableSelection);
      },
    },
    {
      key: ActionIds.Help,
      text: Messages.actions.addSubscriptionHelp(),
      icon: MonochromeIconIds.Help,
      onClick: () => {
        trackActionClick(ActionIds.Help, PageId.CLOUDLINK_LIST);
        setSidePanelOpen(NewSidePanels.GET_STARTED);
      },
    },
  ];
  const cloudLinkInfoBlocks: InfoBlockProps[] = [];
  if (activeCloudLinkItemsForRegionAdd?.length === 0) {
    cloudLinkInfoBlocks.push({
      message: Messages.hints.regionAddNotAvailable(),
      messageType: InfoBlockStatus.INFO,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedItems = (selectedItems: any[]): void => {
    setSelection(selectedItems);
  };

  return (
    <>
      <Listing
        listingComponentRef={setListingComponentRef}
        items={items}
        listColumns={columns}
        itemKey={ColumnIds.Name}
        itemsPerPage={perPageItemsCount}
        actionBarItems={cloudLinkActions}
        onActionClick={closePanels}
        infoBlocks={cloudLinkInfoBlocks}
        selectionMode={SelectionMode.single}
        selectedItems={setSelectedItems}
        emptyList={{
          title: Messages.listCloudLink.emptyList.title(),
          emptyButtons: [
            {
              type: EmptyListingButtonTypes.PRIMARY,
              text: Messages.actions.getStarted(),
              action: () => setSidePanelOpen(NewSidePanels.GET_STARTED),
            },
          ],
          watermark: SvgIconIds.exaWaterMarkSvg,
        }}
        isLoading={subscriptionsLoading || cloudLinksLoading}
        sorting={{
          locale,
          initialSortedColumn: ColumnIds.Name,
        }}
        groupingSelect={{
          groupingOptions,
          defaultSelectedKey: GroupingKeys.NoGrouping,
        }}
        viewSelect={{
          viewOptions,
          defaultSelectedKey: ViewKeys.ListView,
        }}
      />
      {sidePanelOpen === NewSidePanels.GET_STARTED && (
        <ListSubscriptionsInstructionsPanel
          onClose={() => setSidePanelOpen(undefined)}
        />
      )}
    </>
  );
};
